import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PropertyCard } from './PropertyCard';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/lib/supabase';
import { withTimeout } from '@/lib/with-timeout';
import { useRole } from '@/contexts/RoleContext';
import { TopBar } from '@/components/TopBar';
import { useCleanerDashboardProperties } from '@/hooks/useCleanerDashboardProperties';
import { CLEANER_PROPERTIES_QUERY_KEY } from '@/lib/cleanerQueryKeys';
import type { CleanerPropertyWithDetails } from '@/lib/fetchCleanerDashboardProperties';
import { CleanerEquipmentQueue } from '@/components/equipment/CleanerEquipmentQueue';
import { ExtraPaidJobsBanner } from '@/components/cleaner/ExtraPaidJobsBanner';

type PropertyWithDetails = CleanerPropertyWithDetails;

// Session Guard (visibility / długi background)
const waitForSessionReady = async (maxWaitTime = 5000): Promise<boolean> => {
  try {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
      const {
        data: { session },
      } = await withTimeout(supabase.auth.getSession(), 2000);
      if (session) return true;
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return false;
  } catch (error) {
    console.warn('[CleanerDashboard] Błąd podczas sprawdzania sesji:', error);
    return false;
  }
};

export const CleanerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrgId, isLoading: isRoleLoading } = useRole();
  const queryClient = useQueryClient();

  const { data: sessionUserId, isPending: sessionPending } = useQuery({
    queryKey: ["cleaner-session-user-id"],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      return data.user?.id ?? "";
    },
    staleTime: Infinity,
  });

  const {
    data: propertiesData,
    isPending,
    error: queryError,
    refetch,
  } = useCleanerDashboardProperties(sessionUserId, currentOrgId, isRoleLoading);

  const propertiesQueryEnabled =
    Boolean(sessionUserId && currentOrgId) && !isRoleLoading;
  const showListLoader =
    isRoleLoading ||
    sessionPending ||
    (propertiesQueryEnabled && isPending && propertiesData === undefined);

  const properties = propertiesData ?? [];
  const listError = queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  const previousPropertiesRef = useRef<PropertyWithDetails[]>([]);
  const lastHiddenTimeRef = useRef<number | null>(null);

  useEffect(() => {
    previousPropertiesRef.current = properties;
  }, [properties]);

  useEffect(() => {
    const cleanupBodyLock = () => {
      document.body.style.pointerEvents = "auto";
      document.body.style.overflow = "auto";
      document.body.removeAttribute("data-radix-scroll-lock-scrollbar-size");
      document.body.removeAttribute("data-radix-scroll-lock-content");
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        lastHiddenTimeRef.current = Date.now();
      } else if (document.visibilityState === "visible") {
        cleanupBodyLock();
        const hiddenTime = lastHiddenTimeRef.current;
        lastHiddenTimeRef.current = null;
        const propertiesEmpty =
          !previousPropertiesRef.current || previousPropertiesRef.current.length === 0;
        const forceRefresh = propertiesEmpty;

        if (hiddenTime && !forceRefresh) {
          const timeInBackground = Date.now() - hiddenTime;
          const FIVE_MINUTES = 5 * 60 * 1000;
          if (timeInBackground < FIVE_MINUTES) {
            return;
          }
        }

        waitForSessionReady()
          .then((isReady) => {
            if (isReady && currentOrgId && !isRoleLoading) {
              void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
            }
          })
          .catch(console.error);
      } else {
        cleanupBodyLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      cleanupBodyLock();
    };
  }, [currentOrgId, isRoleLoading, queryClient]);

  const handlePropertyClick = (property: PropertyWithDetails) => {
    navigate(`/property/${property.id}`, { state: { property } });
  };

  // Calculate totals - suma zadań pozostałych do wykonania
  const totalRemainingTasks = Array.isArray(properties)
    ? properties.reduce((sum, p) => sum + (p?.tasksRemaining || 0), 0)
    : 0;

  // Group properties by status
  const inProgressProperties = Array.isArray(properties)
    ? properties.filter(p => p?.status === 'in_progress')
    : [];
  const pendingProperties = Array.isArray(properties)
    ? properties
        .filter(p => p?.status === 'pending')
        .sort((a, b) => {
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return (priorityOrder[a?.priority || 'low'] || 3) - (priorityOrder[b?.priority || 'low'] || 3);
        })
    : [];
  const completedProperties = Array.isArray(properties)
    ? properties.filter(p => p?.status === 'completed')
    : [];

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <TopBar />
      <main className="flex-1 p-6">
        <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="work-page-header">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm font-medium text-slate-600 dark:text-gray-300">
            {totalRemainingTasks > 0 ? (
              <>Pozostało: {totalRemainingTasks} {totalRemainingTasks === 1 ? 'zadanie' : totalRemainingTasks < 5 ? 'zadania' : 'zadań'}</>
            ) : (
              <>Wszystkie zadania zrealizowane</>
            )} • {Array.isArray(properties) ? properties.filter(p => p?.status !== 'completed').length : 0} {t('dashboard.propertiesToHandle')}
          </p>
        </div>

        {/* Loading state - tylko dla listy kart, nie przerywa renderowania całego komponentu */}
        {showListLoader ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground dark:text-gray-400">Ładowanie...</p>
            </div>
          </div>
        ) : listError ? (
          /* Error state - tylko dla listy kart */
          <div className="text-center py-16">
            <div className="text-center max-w-md mx-auto px-4">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold mb-2 dark:text-white">{t('dashboard.errorLoading')}</h2>
              <p className="text-muted-foreground dark:text-gray-400 mb-4">{listError}</p>
              <button
                onClick={() => void refetch()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                {t('common.tryAgain')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <ExtraPaidJobsBanner orgId={currentOrgId} userId={sessionUserId} />
            {/* In Progress Section */}
            {inProgressProperties.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  <h2 className="work-section-label">
                    {t('status.inProgress')}
                  </h2>
                </div>
                <div className="space-y-3">
                  {inProgressProperties.map((property) => (
                    <PropertyCard 
                      key={property.id} 
                      property={property} 
                      onClick={() => handlePropertyClick(property)} 
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Pending Section */}
            {pendingProperties.length > 0 && (
              <section className="space-y-3">
                <h2 className="work-section-label">
                  {t('status.pending')}
                </h2>
                <div className="space-y-3">
                  {pendingProperties.map((property) => (
                    <PropertyCard 
                      key={property.id} 
                      property={property} 
                      onClick={() => handlePropertyClick(property)} 
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Completed Section */}
            {completedProperties.length > 0 && (
              <section className="space-y-3">
                <Separator className="bg-border/50 dark:bg-border/20" />
                <h2 className="work-section-label">
                  {t('status.completed')}
                </h2>
                <div className="space-y-3">
                  {completedProperties.map((property) => (
                    <PropertyCard 
                      key={property.id} 
                      property={property} 
                      onClick={() => handlePropertyClick(property)} 
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {(!Array.isArray(properties) || properties.length === 0) && (
              <div className="text-center py-16">
                <div className="h-16 w-16 rounded-full bg-muted dark:bg-[#1a2034] mx-auto flex items-center justify-center mb-4">
                  <span className="text-2xl">✨</span>
                </div>
                <p className="text-lg font-medium dark:text-white">{t('dashboard.noAssignedProperties')}</p>
                <p className="text-muted-foreground dark:text-gray-400 mt-1">{t('dashboard.askCoordinator')}</p>
              </div>
            )}
          </>
        )}

        {currentOrgId && sessionUserId ? (
          <CleanerEquipmentQueue orgId={currentOrgId} userId={sessionUserId} />
        ) : null}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-white dark:bg-[#0C1020] py-4 mt-auto">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground dark:text-gray-500">
          © 2026 DOMIO Cleaning. Wszelkie prawa zastrzeżone.
        </div>
      </footer>
    </div>
  );
};
